import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EDataTableType, EModuleType, EReportType } from '../../data/Constants';
import { MaintenanceTable } from '../DataTable/DataTable';
import { getReports, resetReports, createReport } from '../../store/states/ReportSlice';
import { ReportApi } from '../../api/ReportApi';
import { useDispatch } from 'react-redux';
import { setAlertMessage } from '../../store/states/AlertMessageSlice';
import { AppDispatch, AppStore } from '../../store';
import type { IAPIResponse, IManagementProps } from '../../../types';

/**
 * Handle the outcome of a ProCoS for PPL report generation request.
 *
 * @param res - API response returned by the generate report thunk
 * @param failMessage - Fallback message shown when the response carries no error message
 * @param successMessage - Message shown when the report was generated successfully
 * @param dispatch - Redux dispatch function
 */
function handleApiResponse(
  res: IAPIResponse,
  failMessage: string,
  successMessage: string,
  dispatch: AppDispatch
): void {
  const data = AppStore.getState().reports;
  if (res?.data) {
    void dispatch(
      getReports({
        page: data.currentPage,
        limit: data.recordsPerPage,
        searchCode: data.searchKey,
        reportType: EDataTableType.ProcosForPpl,
      })
    );
    dispatch(setAlertMessage({ show: true, message: successMessage, type: 'success' }));
  } else {
    const message = res?.error?.message ? res.error.message : failMessage;
    dispatch(setAlertMessage({ show: true, message, type: 'error' }));
  }
}

/**
 * ProCoS for PPL report screen with the report history table and a generate control.
 *
 * @param props - Component props
 * @param props.token - Auth token forwarded to the maintenance table
 * @returns The ProCoS for PPL report view, or null while the report state is being reset
 */
export const ProcosForPplManagement = (props: IManagementProps): React.JSX.Element | null => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isReset, setIsReset] = useState(false);

  // The report state is shared by every report type, so clear the previously loaded report
  // before the table mounts; otherwise the table reuses the cached rows and skips its first read
  useEffect(() => {
    dispatch(resetReports());
    setIsReset(true);
    return () => {
      dispatch(resetReports());
    };
  }, [dispatch]);

  /**
   * Trigger generation of a new ProCoS for PPL report and dispatch the result as an alert.
   */
  const handleGenerateReport = (): void => {
    dispatch(createReport(EReportType.ProcosForPpl)).then((res) => {
      handleApiResponse(
        res.payload as IAPIResponse,
        t('messages.fail.create'),
        t('messages.success.create'),
        dispatch
      );
    });
  };

  /**
   * Download the generated ProCoS for PPL report file.
   *
   * @param param - Object containing the report history id
   */
  const handleDownloadReport = (param: { id: string }): void => {
    ReportApi.downloadReport({ id: param.id, reportType: EReportType.ProcosForPpl });
  };

  return (
    <>
      <h3 className="mainHeader">{t('labels.procosForPpl')}</h3>
      {isReset && (
        <MaintenanceTable
          token={props.token}
          type={EDataTableType.ProcosForPpl}
          getDataList={getReports}
          moduleType={EModuleType.Reports}
          generateReport={handleGenerateReport}
          download={handleDownloadReport}
        />
      )}
    </>
  );
};
